import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOrders } from "@/api/hooks/use-orders";
import { Search, X, Package, Rocket, ArrowRight } from "lucide-react";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { OrderCard } from "@/features/orders/components/OrderCard";
import { cn } from "@/lib/utils";
import Button from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fashionPaths } from "@/modules";

type StatusFilter = "all" | "inProgress" | "cancelled";

// OrderGroupStatuses: active | completed | canceled | refunded
const getStatusCategory = (
  statusRaw: string | object | undefined
): StatusFilter => {
  if (!statusRaw) return "inProgress";

  const status =
    typeof statusRaw === "string"
      ? statusRaw.toLowerCase()
      : String(statusRaw).toLowerCase();

  if (["canceled", "cancelled", "refunded"].includes(status)) {
    return "cancelled";
  }

  // active and completed both show under "inProgress" filter (Aktif tab)
  return "inProgress";
};

export const MyOrdersTab: React.FC = () => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch orders list
  const { data: ordersResponse, isLoading, isError, error } = useOrders();

  // Extract orders from response
  const orders = ordersResponse?.data || [];

  // Filter orders by status
  const filteredByStatus = useMemo(() => {
    if (statusFilter === "all") return orders;
    return orders.filter((order) => {
      const category = getStatusCategory((order as any).status);
      return category === statusFilter;
    });
  }, [orders, statusFilter]);

  // Filter orders by search query
  const filteredOrders = useMemo(() => {
    if (!searchQuery.trim()) return filteredByStatus;
    const query = searchQuery.toLowerCase();
    return filteredByStatus.filter((order) => {
      return (
        order.orderGroupId.toLowerCase().includes(query) ||
        (order.recipientName?.toLowerCase().includes(query) ?? false)
      );
    });
  }, [filteredByStatus, searchQuery]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="p-12 text-center">
        <h2 className="text-xl font-semibold text-text-primary dark:text-text-primary mb-2">
          {t("orders.list.errorTitle")}
        </h2>
        <p className="text-text-tertiary dark:text-text-tertiary">
          {error?.message || t("orders.list.errorMessage")}
        </p>
      </div>
    );
  }

  // Empty state
  if (orders.length === 0) {
    return (
      <div className="flex flex-col">
        {/* Header with Title */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5 md:gap-4 px-4 md:px-6 py-3 md:py-5 bg-bg-tertiary dark:bg-gray-800 border-b border-border-secondary dark:border-gray-600">
          <h2 className="text-base md:text-xl font-semibold text-text-primary dark:text-text-primary">
            {t("common.myOrders")}
          </h2>
        </div>

        {/* Empty State Content */}
        <div className="flex flex-col items-center justify-center px-4 py-12 md:py-24">
          {/* Empty State Icon */}
          <div className="bg-bg-tertiary dark:bg-gray-700 rounded-full p-2.5 md:p-3 mb-2.5 md:mb-3">
            <Package className="w-5 h-5 text-text-tertiary dark:text-gray-400" />
          </div>

          {/* Empty State Text */}
          <p className="text-sm md:text-base text-text-secondary dark:text-gray-300 mb-5 md:mb-6 text-center">
            {t("orders.list.emptyMessage")}
          </p>

          {/* Explore Products Button */}
          <Button asChild variant="darkPrimary" className="text-xs md:text-sm">
            <Link to={fashionPaths.shop()}>
              <Rocket className="w-4 h-4 md:w-5 md:h-5" />
              <span className="font-semibold">
                {t("orders.list.startShopping")}
              </span>
              <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header with Title and Search */}
      <div className="flex flex-col gap-2.5 md:gap-4 px-4 md:px-6 py-3 md:py-5 bg-bg-tertiary dark:bg-gray-800 border-b border-border-secondary dark:border-gray-600">
        {/* Title and Search Row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5 md:gap-4">
          <h2 className="text-base md:text-xl font-semibold text-text-primary dark:text-text-primary">
            {t("common.myOrders")}
          </h2>
          <div className="relative w-full md:w-auto md:min-w-[322px]">
            <div className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2">
              <Search className="h-3.5 w-3.5 md:h-4 md:w-4 text-text-tertiary dark:text-gray-400" />
            </div>
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                t("orders.list.searchPlaceholder") || "Search product name"
              }
              className="w-full pl-8 md:pl-10 pr-8 md:pr-10 py-1.5 md:py-2.5 bg-white dark:bg-gray-900 border border-border-secondary dark:border-gray-600 rounded-lg text-xs md:text-sm text-text-secondary dark:text-gray-100 placeholder:text-text-tertiary dark:placeholder:text-gray-400 ring-0 focus:ring-0 focus:border-indigo-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2"
              >
                <X className="h-3.5 w-3.5 md:h-4 md:w-4 text-text-tertiary dark:text-gray-400 hover:text-text-secondary dark:hover:text-gray-300" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5 md:gap-3 px-4 md:px-6 py-2.5 md:py-4 overflow-x-auto scrollbar-hide">
        {[
          { key: "all" as StatusFilter, label: t("orders.list.filters.all") },
          {
            key: "inProgress" as StatusFilter,
            label: t("orders.list.filters.active") || "In Progress",
          },
          {
            key: "cancelled" as StatusFilter,
            label: t("orders.list.filters.cancelled"),
          },
        ].map((filter) => {
          const isActive = statusFilter === filter.key;
          return (
            <button
              key={filter.key}
              onClick={() => setStatusFilter(filter.key)}
              className={cn(
                "px-2.5 md:px-4 py-1 md:py-2 rounded-full text-[10px] md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0",
                isActive
                  ? "border border-indigo-400 text-indigo-400"
                  : "border border-border-secondary dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-400"
              )}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      {/* Orders List */}
      <div className="flex flex-col gap-2.5 md:gap-4 px-4 md:px-6 pb-4 md:pb-6">
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => (
            <OrderCard key={order._id} order={order} />
          ))
        ) : (
          <div className="rounded-xl border border-border-secondary dark:border-gray-600 px-4 py-6 md:py-12 text-center">
            <p className="text-xs md:text-sm font-medium text-text-secondary dark:text-text-secondary mb-1">
              {t("orders.list.emptyFilterTitle")}
            </p>
            <p className="text-xs md:text-sm text-text-tertiary dark:text-gray-400">
              {t("orders.list.emptyFilterMessage")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
